import { isNullOrUndifined } from 'admin/utils';
import {
  isSamePointExpiresAt,
  toPointExpiresAtFormValue,
  toPointExpiresAtParam,
} from 'admin/utils/pointExpiry';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { fetchCampaign, fetchCampaignSummary, updateCampaign } from 'admin/actions/campaign';
import Headings from 'admin/components/atoms/Headings';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import _ from 'lodash';
import CampaignModal from '../../pages/CampaignModal';
import EditCampaign, {
  CampaignForm,
  toShopLabelSelections,
  toShopSpecification,
} from './EditCampaign';

const FormWrapper = styled.div``;

const crumb = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '販促管理', href: '', isCurrentPage: true },
  { value: 'キャンペーン管理', href: '/promotion/campaigns', isCurrentPage: false },
  { value: 'キャンペーン詳細', href: '', isCurrentPage: true },
];

const isBool = (v: unknown) => {
  if (_.isBoolean(v)) return v;
  if (v === 'TRUE') return true;
  return false;
};

const CampaignDetail = ({ match }) => {
  const campaignId = match.params.id;
  const [campaign, setCampaign] = useState<CampaignForm | null>(null);
  const [campaignSummary, setCampaignSummary] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiMessages, setApiMessages] = useState<{ text: string; isError?: boolean }[]>([]);
  const [requestParams, setRequestParams] = useState<any>({});
  const [campaignForm, setCampaignForm] = useState<CampaignForm | null>(null);

  useEffect(() => {
    fetchCampaign(campaignId).then((c) => {
      setCampaignStatus(c);
      (async () =>
        fetchCampaignSummary(campaignId).then((c) => {
          setCampaignSummary({ ...campaign, ...c });
        }))();
    });
  }, []);

  const setCampaignStatus = (c: Awaited<ReturnType<typeof fetchCampaign>>) => {
    const { point_calculation_rule_object } = c;
    const pointCalculationRule = JSON.parse(point_calculation_rule_object);

    const amountBasedRules = pointCalculationRule.amount_based_point_rules || [];
    const firstAmountBasedRule = amountBasedRules.length > 0 ? amountBasedRules[0] : undefined;
    const amountBasedType =
      amountBasedRules.length > 1 ||
      firstAmountBasedRule?.subject_less_than_or_equal ||
      firstAmountBasedRule?.subject_more_than_or_equal
        ? 'custom'
        : 'uniform';

    const productBasedRules = pointCalculationRule.product_based_point_rules || [];

    const getResidentCheck = (metaData?: {
      key?: string;
      value?: string;
      test?: string;
    }): CampaignForm['residentCheck'] => {
      if (metaData?.key !== 'is_resident' || metaData?.value !== 'true') {
        return 'none';
      }

      return metaData?.test === 'not-equal' ? 'optional' : 'required';
    };

    const residentCheck = getResidentCheck(pointCalculationRule?.applicable_account_metadata);

    const getSelectedPointRuleType = () => {
      if (!pointCalculationRule.amount_based_point_rules) {
        return 'product';
      }
      if (amountBasedType === 'custom') {
        if (pointCalculationRule.subject === 'money') {
          return 'moneyAmountCustom';
        }
        return 'allAmountCustom';
      }
      if (pointCalculationRule.subject === 'money') {
        return 'moneyAmountUniform';
      }
      return 'allAmountUniform';
    };

    const fetchedCampaign: CampaignForm = {
      id: c.id,
      privateMoney: c.private_money,
      pointBearerShop: {
        id: c.bear_point_shop_uuid,
        name: c.bear_point_shop_name,
      },
      organization: c.organization,
      name: c.name,
      description: c.description,
      priority: c.priority,
      isExclusive: c.is_exclusive,
      event: pointCalculationRule.event ?? null,
      applicableTransactionMetadata:
        pointCalculationRule.applicable_transaction_metadata?.value ?? null,
      dateRange: {
        from: moment(c.starts_at),
        to: moment(c.ends_at),
      },
      pointExpiresAt: toPointExpiresAtFormValue(c.point_expires_at),
      pointExpiresInDays: c.point_expires_in_days,
      daysOfWeek: pointCalculationRule.applicable_days_of_week || [],
      timeRanges: pointCalculationRule.applicable_time_ranges || [],
      amountBasedPointRules: {
        uniformAmountRule: {
          amount: amountBasedType === 'uniform' ? firstAmountBasedRule?.point_amount ?? null : null,
          unit: firstAmountBasedRule?.point_amount_unit ?? 'percent',
          from: null,
          to: null,
        },
        customAmountRules:
          amountBasedType === 'custom'
            ? amountBasedRules.map((x) => ({
                amount: x.point_amount,
                unit: x.point_amount_unit,
                from: x.subject_more_than_or_equal,
                to: x.subject_less_than_or_equal,
              }))
            : [
                {
                  amount: null,
                  unit: 'percent',
                  from: null,
                  to: null,
                },
              ],
      },
      productBasedPointRules: {
        miniumNumberForCombinationPurchase:
          pointCalculationRule.minimum_number_for_combination_purchase,
        productBasedPointRules: productBasedRules.map((x) => ({
          ...x,
          is_multiply_by_count: isBool(x.is_multiply_by_count),
        })),
      },
      selectedPointRulesType: getSelectedPointRuleType(),
      applicableShops: c.shops || c.blacklisted_shops || [],
      applicableShopLabels: toShopLabelSelections(c.shop_labels),
      budgetCapsAmount: c.budget_caps_amount ?? null,
      maxTotalPointAmount: c.max_total_point_amount ?? null,
      maxPointAmount: pointCalculationRule.max_point_amount ?? null,
      shopSpecification: toShopSpecification(c),
      residentCheck,
    };

    setCampaign(fetchedCampaign);
  };

  // Captured here because onSubmit's parameter shadows the campaign state
  const originalPointExpiresAt = campaign?.pointExpiresAt ?? null;

  const onSubmit = (campaign: CampaignForm) => {
    setApiMessages([]);

    if (
      campaign.description === '条件付きキャンペーン' &&
      campaign.privateMoney.organization?.code.startsWith('univcoop-')
    ) {
      setApiMessages([
        {
          text: 'このキャンペーンは編集できません',
          isError: true,
        },
      ]);
      return;
    }

    const params = {
      name: campaign.name,
      description: campaign.description,
      /* private_money_id: campaign.privateMoney.id, */
      starts_at: campaign.dateRange?.from,
      ends_at: campaign.dateRange?.to,
      /* bear_point_shop_id: campaign.pointBearerShop?.id, */
      is_exclusive: campaign.isExclusive,
      event: campaign.event,
      priority: parseInt(campaign.priority?.toString() ?? '0', 10),
      subject:
        campaign.event === 'external-transaction' ||
        !['moneyAmountUniform', 'moneyAmountCustom'].includes(campaign.selectedPointRulesType)
          ? 'all'
          : 'money',
      applicable_transaction_metadata:
        campaign.event === 'topup' && campaign.applicableTransactionMetadata
          ? {
              key: 'topup-method-type',
              value: campaign.applicableTransactionMetadata,
            }
          : null,
      budget_caps_amount: campaign.budgetCapsAmount ? Number(campaign.budgetCapsAmount) : null,
      max_total_point_amount: campaign.maxTotalPointAmount
        ? Number(campaign.maxTotalPointAmount)
        : null,
      max_point_amount: campaign.maxPointAmount ? Number(campaign.maxPointAmount) : null,
      applicable_account_metadata:
        campaign.residentCheck === 'none'
          ? null
          : {
              key: 'is_resident',
              value: 'true',
              ...(campaign.residentCheck === 'required' ? {} : { test: 'not-equal' }),
            },
      applicable_shop_ids:
        campaign.shopSpecification === 'whitelist' && campaign.applicableShops.length > 0
          ? campaign.applicableShops.map((s) => s.id)
          : null,
      blacklisted_shop_ids:
        campaign.shopSpecification === 'blacklist' && campaign.applicableShops.length > 0
          ? campaign.applicableShops.map((s) => s.id)
          : null,
      applicable_shop_label_ids:
        campaign.shopSpecification === 'labels'
          ? campaign.applicableShopLabels.flatMap((r) => r.itemIds)
          : null,
      // Not sent unless the expiry was touched: sending it rewrites Partner-API values to the
      // admin convention (next day 00:00:00), moving the expiry ~24h earlier on a name-only save
      ...(isSamePointExpiresAt(campaign.pointExpiresAt, originalPointExpiresAt)
        ? {}
        : { point_expires_at: toPointExpiresAtParam(campaign.pointExpiresAt) }),
      point_expires_in_days: Number.isFinite(
        parseInt(campaign.pointExpiresInDays?.toString() ?? '')
      )
        ? Number(campaign.pointExpiresInDays)
        : null,
      applicable_days_of_week: campaign.daysOfWeek.length ? campaign.daysOfWeek : null,

      applicable_time_ranges: campaign.timeRanges.length ? campaign.timeRanges : null,
      ...(['allAmountUniform', 'moneyAmountUniform'].includes(campaign.selectedPointRulesType) && {
        amount_based_point_rules: [
          {
            point_amount: Number(campaign.amountBasedPointRules.uniformAmountRule.amount),
            point_amount_unit: campaign.amountBasedPointRules.uniformAmountRule.unit,
          },
        ],
      }),
      ...(['allAmountCustom', 'moneyAmountCustom'].includes(campaign.selectedPointRulesType) && {
        amount_based_point_rules: campaign.amountBasedPointRules.customAmountRules.map((rule) => ({
          ...(rule.amount !== null && { point_amount: rule.amount }),
          ...(rule.unit && { point_amount_unit: rule.unit }),
          ...(rule.from !== null && { subject_more_than_or_equal: rule.from }),
          ...(rule.to !== null && { subject_less_than_or_equal: rule.to }),
        })),
      }),
      ...(campaign.selectedPointRulesType === 'product' && {
        product_based_point_rules: campaign.productBasedPointRules.productBasedPointRules,
      }),
      ...(campaign.selectedPointRulesType === 'product' &&
        campaign.productBasedPointRules.productBasedPointRules.length > 1 &&
        campaign.productBasedPointRules.miniumNumberForCombinationPurchase && {
          minimum_number_for_combination_purchase: Number(
            campaign.productBasedPointRules.miniumNumberForCombinationPurchase
          ),
        }),
    };

    setCampaignForm(campaign);
    setRequestParams(params);
    setIsModalOpen(true);
  };

  const onConfirm = () => {
    updateCampaign(campaignId, requestParams)
      .then(() => {
        setApiMessages([
          {
            text: 'Registered a campaign: キャンペーンを更新しました',
          },
        ]);
        setCampaign(campaignForm);
      })
      .catch((err) => {
        setApiMessages([
          {
            text: `Could not updated a campaign: ${err.message}.`,
            isError: true,
          },
        ]);
      });
    setIsModalOpen(false);
  };

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumb} />
      <Headings.h5>キャンペーン情報</Headings.h5>
      {isNullOrUndifined(campaign) ? null : (
        <EditCampaign
          mode="edit"
          campaign={campaign ?? undefined}
          campaignSummary={campaignSummary ?? undefined}
          onSubmit={onSubmit}
          messages={apiMessages}
        />
      )}

      <CampaignModal
        isOpen={isModalOpen}
        campaign={campaignForm}
        comparingCampaign={campaign}
        onClose={() => setIsModalOpen(false)}
        onConfirm={onConfirm}
      />
    </FormWrapper>
  );
};

export default CampaignDetail;
